import classNames from 'classnames';
import { BaseSyntheticEvent } from 'react';
import Icon, { IconProps } from '../Icon';

type Colors =
  | 'primary'
  | 'primary-outline'
  | 'secondary'
  | 'secondary-pale'
  | 'secondary-outline'
  | 'accent'
  | 'accent-outline'
  | 'white'
  | 'white-outline'
  | 'transparent-white'
  | 'gray-outline'
  | 'green-wa'
  | 'green-wa-outline'
  | 'blue-fb'
  | 'blue-fb-outline'
  | 'blue-in'
  | 'blue-in-outline';

export interface Props
  extends React.ButtonHTMLAttributes<
    HTMLButtonElement | HTMLLinkElement | HTMLAnchorElement
  > {
  as?: 'a' | 'button';
  id?: string;
  size?: 'sm' | 'md' | 'lg';
  color?: Colors;
  text?: string;
  iconName?: IconProps;
  iconPosition?: 'right' | 'left';
  href?: string;
  fullRounded?: boolean;
  smallText?: string;
  smallTextStyle?: Colors;
  shadow?: boolean;
  onClick?: (e: BaseSyntheticEvent) => void;
  fluid?: boolean;
  disabled?: boolean;
  className?: string;
}

const getColorClass = (
  color: Props['color']
): string | undefined => {
  switch (color) {
    case 'primary':
      return 'bg-sl-indigo border-sl-indigo text-white hover:bg-sl-indigo-dark  hover:border-sl-indigo-dark';

    case 'secondary':
      return 'bg-sl-blue border-sl-blue hover:border-sl-blue-dark hover:bg-sl-blue-dark text-white';

    case 'secondary-pale':
      return 'bg-sl-blue bg-opacity-10 border-sl-blue border-opacity-10 hover:border-sl-blue-dark  hover:bg-sl-blue-dark hover:bg-opacity-20 hover:border-opacity-20 bg-clip-padding';

    case 'accent':
      return 'bg-sl-yellow border-sl-yellow text-sl-blue hover:bg-sl-yellow-dark  hover:border-sl-yellow-dark';

    case 'white':
      return 'bg-white border-white text-sl-blue hover:bg-sl-blue hover:border-sl-blue hover:text-white';

    case 'white-outline':
      return 'bg-transparent border-white text-white hover:bg-white hover:text-sl-blue';

    case 'primary-outline':
      return 'bg-transparent border-sl-indigo text-sl-indigo hover:bg-sl-indigo hover:border-sl-indigo hover:text-white';

    case 'secondary-outline':
      return 'bg-transparent border-sl-blue text-sl-blue hover:border-sl-blue hover:bg-sl-blue hover:text-white';

    case 'transparent-white':
      return 'bg-white bg-clip-padding bg-opacity-10 border-white border-opacity-10 hover:border-opacity-30 text-white hover:bg-opacity-30 hover:text-white';

    case 'gray-outline':
      return 'border-sl-blue-dark color-sl-blue border-opacity-25 hover:border-opacity-100 hover:bg-sl-blue-dark hover:text-white';

    case 'accent-outline':
      return 'bg-transparent border-sl-yellow text-sl-yellow hover:bg-sl-yellow  hover:border-sl-yellow hover:text-white';

    case 'green-wa':
      return 'bg-sl-green-wa border-sl-green-wa text-white hover:opacity-80';

    case 'green-wa-outline':
      return 'bg-transparent border-sl-green-wa text-sl-green-wa hover:bg-sl-green-wa hover:text-white';

    case 'blue-fb':
      return 'bg-sl-blue-fb border-sl-blue-fb text-white hover:opacity-80';

    case 'blue-fb-outline':
      return 'bg-transparent border-sl-blue-fb text-sl-blue-fb hover:bg-sl-blue-fb hover:text-white';

    case 'blue-in':
      return 'bg-sl-blue-in border-sl-blue-in text-white hover:opacity-80';

    case 'blue-in-outline':
      return 'bg-transparent border-sl-blue-in text-sl-blue-in hover:bg-sl-blue-in hover:text-white';

    default:
      return '';
  }
};

const getSizeClass = (size: string): string | undefined => {
  switch (size) {
    case 'sm':
      return 'text-xs md:text-sm';

    case 'md':
      return 'text-sm md:text-base';

    case 'lg':
      return 'text-base md:text-xl';

    default:
      return '';
  }
};

function Button({
  as: Tag = 'button',
  id,
  size = 'md',
  color = 'primary',
  text = '',
  iconName,
  iconPosition = 'right',
  href,
  fullRounded = false,
  smallText,
  smallTextStyle,
  shadow = false,
  onClick,
  fluid = false,
  disabled = false,
  className,
  ...rest
}: Props) {
  return (
    <Tag
      className={classNames(
        'text-bold inline-flex items-center justify-center border-2 p-3.5 font-bold leading-tight transition-all duration-200 md:leading-tight',
        { 'rounded-full': fullRounded },
        { 'rounded-xl': !fullRounded },
        { relative: smallText },
        getColorClass(color),
        getSizeClass(size),
        { 'flex-row-reverse': iconPosition === 'left' },
        [shadow && 'shadow-button-depth-1'],
        {
          'w-full justify-center gap-4 text-center': fluid
        },
        {
          'pointer-events-none cursor-not-allowed !border-opacity-0 !bg-sl-blue !bg-opacity-10 !text-sl-blue-dark !opacity-25':
            disabled
        },
        [className]
      )}
      href={Tag === 'a' && href ? href : undefined}
      id={id}
      onClick={onClick}
      disabled={disabled}
      {...rest}
    >
      {text}

      {iconName && (
        <Icon
          icon={iconName}
          className={classNames(
            'min-h-[1.25em] min-w-[1.25em]',
            {
              'mr-4':
                iconPosition === 'left' && text && !fluid,
              'ml-4':
                iconPosition === 'right' && text && !fluid
            },
            [
              fluid &&
                iconPosition === 'left' &&
                text &&
                'mr-0'
            ],
            [
              fluid &&
                iconPosition === 'right' &&
                text &&
                'ml-0'
            ]
          )}
        />
      )}
      {smallText && (
        <span className="absolute top-full left-0 right-0 mt-[2px] break-words text-center font-circular text-[10px]">
          <span
            className={classNames([
              'pointer-events-none inline-block rounded-bl-md rounded-br-md py-1 px-3 align-top font-bold',
              smallTextStyle &&
                getColorClass(smallTextStyle)
            ])}
          >
            {smallText}
          </span>
        </span>
      )}
    </Tag>
  );
}

export default Button;
